import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryDisciplinesStatusSA } from 'Hooks/disciplines/useQueryDisciplinesStatusSA';
import { useQueryRestoreDisciplineSA } from 'Hooks/disciplines/useQueryRestoreDisciplineSA';
import { useQueryCreateDisciplineSA } from 'Hooks/disciplines/useQueryCreateDisciplineSA';
import { useQueryMergeDisciplineSA } from 'Hooks/disciplines/useQueryMergeDisciplineSA';
import { useQueryDeleteDisciplineSA } from 'Hooks/disciplines/useQueryDeleteDisciplineSA';
import { ApisEnumLabel } from 'Data/enums/Apis';
import { FileStatusInStorageEnum } from 'Data/interfaces/Obra/IDisciplineSAStatusResponse';
import {
  ActionSolutionType,
  ICreateSADisciplineRequest,
  IDeleteSADisciplineRequest,
  IMergeSADisciplineRequest,
  IRestoreSADisciplineRequest,
} from 'Data/interfaces/Obra/ISolutionSADisciplineRequest';
import { Icon, IconName } from 'Components/UI';
import Button from 'Components/UI/Button';
import Modal from 'Components/UI/Modal';
import LoadingDisciplineSAConnection from 'Components/UI/CustomLoading/LoadingDisciplineSAConnection';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import { ButtonsWrapper, Card, Footer, Header, Main, Wrapper } from './styles';

interface ISolutionSAConnection {
  platform: number;
  visible?: boolean;
  discipline: any;
  onCancel: () => void;
}

export interface IActionOption {
  id: ActionSolutionType;
  icon: IconName;
  iconSize: number;
  header: string;
  description: string;
  action: (discipline: any) => void;
}

const SolutionSAConnection: FC<ISolutionSAConnection> = ({
  platform,
  visible,
  discipline,
  onCancel,
}) => {
  const { t } = useTranslation('disciplinas');
  const {
    data,
    isFetching: isLoadingStatus
  } = useQueryDisciplinesStatusSA({
    ConstructionSiteDisciplineId: discipline.ConstructionSiteDisciplinesId,
    fileApiId: discipline.DisciplineApiFolderId,
    csId: discipline.ConstructionSiteFk,
  });
  const {
    isLoading: isRestoreLoading,
    restoreDisciplineSA,
  } = useQueryRestoreDisciplineSA();
  const {
    isLoading: isCreateLoading,
    createDisciplineSA,
  } = useQueryCreateDisciplineSA();
  const {
    isLoading: isMergeLoading,
    mergeDisciplineSA,
  } = useQueryMergeDisciplineSA();
  const {
    isLoading: isDeleteLoading,
    deleteDisciplineSA,
  } = useQueryDeleteDisciplineSA();

  const [showDelete, setShowDelete] = useState(false);
  const [selectedOpt, setSelectedOpt] = useState<ActionSolutionType | null>(null);
  const [sentSolutionSAConnection, setSentSolutionSAConnection] = useState(false);
  const trashed = FileStatusInStorageEnum.Trashed === data?.disciplineSAStatus;
  const merged = FileStatusInStorageEnum.Recreated === data?.disciplineSAStatus;
  const solutionDelete = selectedOpt === 'del';

  const isLoading = useMemo(() => {
    return isRestoreLoading ||
      isCreateLoading ||
      isMergeLoading ||
      isDeleteLoading;
  }, [isCreateLoading, isDeleteLoading, isMergeLoading, isRestoreLoading]);

  const actionOptions: Record<string, IActionOption> = useMemo(() => {
    const options: Record<string, IActionOption> = {
      'rest': {
        id: 'rest',
        action: (useDicipline: any) => {
          const request: IRestoreSADisciplineRequest = {
            ConstructionSiteDisciplinesId: useDicipline.ConstructionSiteDisciplinesId,
            ConstructionSiteId: discipline.ConstructionSiteFk,
          };
          restoreDisciplineSA(request);
        },
        icon: 'relogio',
        iconSize: 26,
        header: t('solutionSAConnection.restoreHeader'),
        description: t('solutionSAConnection.restoreDescription'),
      },
      'new': {
        id: 'new',
        action: (useDicipline: any) => {
          const request: ICreateSADisciplineRequest = {
            ConstructionSiteDisciplinesId: useDicipline.ConstructionSiteDisciplinesId,
            ConstructionSiteId: discipline.ConstructionSiteFk,
          };
          if (data?.existingFolderApiId) {
            request.existingFolderApiId = data.existingFolderApiId;
          }
          createDisciplineSA(request);
        },
        icon: 'adicionarPasta',
        iconSize: 26,
        header: t('solutionSAConnection.createHeader'),
        description: t('solutionSAConnection.createDescription'),
      },
      'merge': {
        id: 'merge',
        action: (useDicipline: any) => {
          const request: IMergeSADisciplineRequest = {
            ConstructionSiteDisciplinesId: useDicipline.ConstructionSiteDisciplinesId,
            ConstructionSiteId: discipline.ConstructionSiteFk,
          };
          if (data?.existingFolderApiId) {
            request.existingFolderApiId = data.existingFolderApiId;
          }
          mergeDisciplineSA(request);
        },
        icon: 'adicionarPasta',
        iconSize: 26,
        header: t('solutionSAConnection.mergeHeader'),
        description: t('solutionSAConnection.mergeDescription'),
      },
      'del': {
        id: 'del',
        action: (useDicipline: any) => {
          const request: IDeleteSADisciplineRequest = {
            ConstructionSiteDisciplinesId: useDicipline.ConstructionSiteDisciplinesId,
            ConstructionSiteId: discipline.ConstructionSiteFk,
          };
          if (data?.existingFolderApiId) {
            request.existingFolderApiId = data.existingFolderApiId;
          }
          deleteDisciplineSA(request);
        },
        icon: 'excluir',
        iconSize: 20,
        header: t('solutionSAConnection.deleteHeader'),
        description: t('solutionSAConnection.deleteDescription'),
      },
    };

    if (!trashed) {
      delete options['rest'];
    }

    if (!merged) {
      delete options['merge'];
    } else {
      delete options['new'];
    }

    return options;
  }, [trashed, merged, data, discipline, t, restoreDisciplineSA, createDisciplineSA, mergeDisciplineSA, deleteDisciplineSA]);

  const handleSelectedOpt = (opt: ActionSolutionType) => {
    setSelectedOpt(opt);
  };

  const handleConfirm = () => {
    if (solutionDelete && !showDelete) {
      setShowDelete(true);
      return setSentSolutionSAConnection(true);
    }

    if (selectedOpt && discipline) {
      actionOptions[selectedOpt].action(discipline);
      return setSentSolutionSAConnection(true);
    }
  };

  const api = ApisEnumLabel[platform];
  const disciplineName = discipline?.CustomName || discipline?.Discipline?.Name;

  const footerButtons = (
    <ButtonsWrapper disabled={`${(isLoading || !selectedOpt)}`}>
      <Button
        className="cancel"
        onClick={onCancel}
      >
        <span className="cancelText">{t('solutionSAConnection.cancel')}</span>
      </Button>
      <Button
        type="primary"
        className="confirm"
        onClick={() => handleConfirm()}
        loading={isLoading && !solutionDelete}
        disabled={isLoading || !selectedOpt}
      >
        <span className="confirmText">{t('solutionSAConnection.confirm')}</span>
      </Button>
    </ButtonsWrapper>
  );

  useEffect(() => {
    if (data?.disciplineSAStatus === FileStatusInStorageEnum.Ok) {
      onCancel();
    }
  }, [data?.disciplineSAStatus]);

  useEffect(() => {
    if (sentSolutionSAConnection && !isLoading) {
      onCancel();
    }
  }, [isLoading, sentSolutionSAConnection]);

  return (
    <Modal
      width={500}
      title={t('solutionSAConnection.modalTitle')}
      visible={visible}
      onCancel={onCancel}
      footerButtons={footerButtons}
    >
      {isLoadingStatus && (
        <LoadingDisciplineSAConnection multiple={3} />
      )}
      {!isLoadingStatus && (
        <Wrapper>
          <Header>
            {trashed && (
              <span>
                {t('solutionSAConnection.headerTrashed', { name: disciplineName, api })}
              </span>
            )}
            {!trashed && (
              merged ? (
                <span>
                  {t('solutionSAConnection.headerMerged', { name: disciplineName, api })}
                </span>
              ) : (
                <span>
                  {t('solutionSAConnection.headerDeleted', { name: disciplineName, api })}
                </span>
              )
            )}
          </Header>

          <Main>
            {Object.values(actionOptions).map(option => {
              const selected = selectedOpt === option.id;

              return (
                <Card
                  key={option.id}
                  selected={`${selected}`}
                  onClick={() => handleSelectedOpt(option.id)}
                >
                  <div className="cardLeft">
                    <Icon
                      color="primary"
                      icon={option.icon}
                      customSize={option.iconSize}
                      blocked={!selected}
                    />
                  </div>
                  <div className="cardRight">
                    <span className="cardHeader">{option.header}</span>
                    <span className="cardDescription">{option.description}</span>
                  </div>
                </Card>
              )
            })}
          </Main>

          <Footer>
            <Icon
              icon="alerta"
              customSize={18}
              customColor={'#E2574C'}
            />
            <span className="footerText">{t('solutionSAConnection.footerWarning')}</span>
          </Footer>
        </Wrapper>
      )}

      {showDelete && (
        <ConfirmDeleteModal
          visible={showDelete}
          onConfirm={() => handleConfirm()}
          onCancel={() => setShowDelete(false)}
          loading={isLoading}
        />
      )}
    </Modal>
  );
}

export default SolutionSAConnection;
